// API 基础配置
// 生产环境使用 Workers URL，开发环境使用 proxy
const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? '/api'
  : 'https://product-annotator-backend.lswuzeru.workers.dev/api';

// 获取存储的 token
function getToken(): string | null {
  const authData = localStorage.getItem('auth-storage');
  if (!authData) return null;
  try {
    const parsed = JSON.parse(authData);
    return parsed.state?.token || null;
  } catch {
    return null;
  }
}

// 通用请求函数
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // 处理 401 未授权
  if (response.status === 401) {
    localStorage.removeItem('auth-storage');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

// API 方法
export const api = {
  // 认证
  auth: {
    login: (email: string, password: string) =>
      request<{ success: boolean; data: { user: any; token: string } }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    register: (email: string, password: string, name?: string) =>
      request<{ success: boolean; data: { user: any; token: string } }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      }),

    me: () => request<{ success: boolean; data: any }>('/auth/me'),
  },

  // 项目
  projects: {
    list: () => request<{ success: boolean; data: any[] }>('/projects'),
    get: (id: string) => request<{ success: boolean; data: any }>(`/projects/${id}`),
    create: (name: string, template?: string) =>
      request<{ success: boolean; data: any }>('/projects', {
        method: 'POST',
        body: JSON.stringify({ name, template }),
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean }>(`/projects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/projects/${id}`, {
        method: 'DELETE',
      }),
  },

  // 图片
  images: {
    list: (projectId: string) =>
      request<{ success: boolean; data: any[] }>(`/images/project/${projectId}`),
    get: (id: string) => request<{ success: boolean; data: any }>(`/images/${id}`),
    preview: (id: string) =>
      request<{ success: boolean; data: { url: string } }>(`/images/${id}/preview`),
    upload: (projectId: string, files: File[]) => {
      const formData = new FormData();
      formData.append('projectId', projectId);
      files.forEach((file) => formData.append('files', file));

      const token = getToken();
      return fetch(`${API_BASE}/images/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      }).then((r) => r.json());
    },
    delete: (id: string) =>
      request<{ success: boolean }>(`/images/${id}`, {
        method: 'DELETE',
      }),
  },

  // 标注
  annotations: {
    start: (projectId: string) =>
      request<{ success: boolean; data: any }>('/annotations/start', {
        method: 'POST',
        body: JSON.stringify({ projectId }),
      }),
    getByImage: (imageId: string) =>
      request<{ success: boolean; data: any }>(`/annotations/image/${imageId}`),
    getByProject: (projectId: string) =>
      request<{ success: boolean; data: any[] }>(`/annotations/project/${projectId}`),
    update: (id: string, result: any, reviewed: boolean) =>
      request<{ success: boolean }>(`/annotations/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ result, reviewed }),
      }),
  },

  // 导出
  export: {
    json: (projectId: string) => `${API_BASE}/export/json/${projectId}`,
    csv: (projectId: string) => `${API_BASE}/export/csv/${projectId}`,
  },
};
